import * as fabric from "fabric";
import { FabricImage } from "fabric";
import store from "../../store";
import { handleFileChange } from "../Toolbar/ImageEditor/components/UploadSelector";
import { checkAndAdjustRotation } from "../Toolbar/ImageEditor/ImageEditor";
import { replaceImageInContainer } from "../Toolbar/ImageEditor/imageToolbarUtils";
import { handleDisplayToolbar } from "../utils";
import {
  addClipPath,
  calculatePosition,
  checkIfInsideClipRect,
  createCustomRectPath,
  getBaseShapeConfig,
  getRotation,
  openUploadImageBox,
  parseBorderRadius,
  updateRadiusProperties
} from "./imagesUtils";
import { placeholderBase64 } from "../../data/placeholder";


const DEBOUNCE_TIME = 1000;

let initialState = {};

/**
 * Checks if the current state of a fabric.js image has changed compared to the initial state, and dispatches an action
 * to update the current data in the application state if changes are detected.
 *
 * @param {fabric.Image} fabricImage - The fabric.js image object to check for changes.
 * @param {function} dispatch - The dispatch function to use for dispatching the update action.
 * @param {string} selectedLayout - The ID of the currently selected layout.
 * @param {React.MutableRefObject<fabric.Canvas | null>} fabricCanvasInstanceRef - A reference to the fabric.js canvas
 *   instance.
 * @returns {function} A function that can be called to perform the change detection and dispatch the update action.
 */
export const checkChangesAndDispatch = ( fabricImage, dispatch, selectedLayout, fabricCanvasInstanceRef ) => {
  return () => {
    const currentState = {
      aCoords: fabricImage.aCoords,
      left: fabricImage.left,
      top: fabricImage.top,
      angle: fabricImage.angle,
      width: fabricImage.width,
      height: fabricImage.height,
      scaleX: fabricImage.scaleX,
      scaleY: fabricImage.scaleY,
      flipX: fabricImage.flipX,
      flipY: fabricImage.flipY,
      src: fabricImage.getSrc()
    };

    const hasChanged = Object.keys( initialState ).some(
      ( key ) => initialState[ key ] !== currentState[ key ]
    );

    if ( hasChanged ) {
      dispatch( {
        type: "editor/updateCurrentDataFabricArray",
        payload: {
          layoutId: selectedLayout,
          fabricElements: JSON.stringify(
            fabricCanvasInstanceRef.current.toJSON()
          )
        }
      } );
      initialState = { ...currentState };
    }
  };
};

/**
 * Configures a fabric.js image object with various properties and event handlers.
 *
 * @param {fabric.Image} fabricImage - The fabric.js image object to configure.
 * @param {any} item - The item object containing the CSS styles to use for the configuration.
 * @param {number} scale - The scale factor to apply to the item's dimensions.
 * @param {number} radius - The radius to use for rounded corners.
 * @param {React.MutableRefObject<fabric.Canvas | null>} fabricCanvasInstanceRef - A reference to the fabric.js canvas
 *   instance.
 * @param {number} borderRadius - The border radius to apply to the image.
 * @param {Function} dispatch - A function to dispatch actions.
 * @param {any} selectedLayout - The selected layout.
 * @param {boolean} [placeholder=false] - Whether the image is a placeholder.
 */
export const configureFabricImage = ( fabricImage,
  item,
  scale,
  fabricCanvasInstanceRef,
  borderRadius,
  dispatch,
  selectedLayout,
  placeholder = false,
  recreated = false ) => {
  const containerRotation = getRotation( item );
  const hasCustomCorners =
    item.style[ "border-bottom-right-radius" ] ||
    item.style[ "border-bottom-left-radius" ] ||
    item.style[ "border-top-right-radius" ] ||
    item.style[ "border-top-left-radius" ];

  const rightTopRadius = parseBorderRadius( item.style[ "border-top-right-radius" ], scale );
  const rightBottomRadius = parseBorderRadius( item.style[ "border-bottom-right-radius" ], scale );
  const leftTopRadius = parseBorderRadius( item.style[ "border-top-left-radius" ], scale );
  const leftBottomRadius = parseBorderRadius( item.style[ "border-bottom-left-radius" ], scale );

  const { hPosition, vPosition, width, height } = calculatePosition( item, scale, fabricCanvasInstanceRef );

  if ( !recreated ) {
    fabricImage.on( "mousedown", ( event ) => {
      fabricCanvasInstanceRef.current.setActiveObject( fabricImage );
      fabricCanvasInstanceRef.current?.requestRenderAll();

      if(fabricImage.placeholderImage) {
      openUploadImageBox( fabricImage, dispatch )( event );
      }
    } );
  }

  let clipRect;
  if (hasCustomCorners) {
    fabricImage.set({
      cornerTopLeft: item.style["border-top-left-radius"] ? parseFloat(item.style["border-top-left-radius"]) : 0,
      cornerTopRight: item.style["border-top-right-radius"] ? parseFloat(item.style["border-top-right-radius"]) : 0,
      cornerBottomLeft: item.style["border-bottom-left-radius"] ? parseFloat(item.style["border-bottom-left-radius"]) : 0,
      cornerBottomRight: item.style["border-bottom-right-radius"] ? parseFloat(item.style["border-bottom-right-radius"]) : 0,
      isRounded: true
    });
    const pathData = createCustomRectPath(width, height, {
      topLeft: leftTopRadius,
      topRight: rightTopRadius,
      bottomLeft: leftBottomRadius,
      bottomRight: rightBottomRadius,
    });

    clipRect = new fabric.Path(pathData, {
      left: Math.round(hPosition),
      top: Math.round(vPosition),
      width: Math.round(width),
      height: Math.round(height),
      originX: "center",
      originY: "center",
      absolutePositioned: true,
      angle: containerRotation,
      objectCaching: false,
    });

    fabricImage.clipPath = clipRect;
    fabricCanvasInstanceRef.current.renderAll();
  } else {
    clipRect = new fabric.Rect({
      left: Math.round(hPosition),
      top: Math.round(vPosition),
      width: Math.round(width),
      height: Math.round(height),
      originX: "center",
      originY: "center",
      absolutePositioned: true,
      angle: containerRotation,
      objectCaching: false,
    });

    if (borderRadius && borderRadius !== 0) {
      const newRadius =
        typeof item.style["border-radius"] === "string" &&
        item.style["border-radius"].includes("%")
          ? (parseFloat(item.style["border-radius"]) / 100) *
            Math.min(clipRect.width, clipRect.height)
          : parseFloat(item.style["border-radius"]) * scale;

      clipRect.rx = newRadius;
      clipRect.ry = newRadius;
    }
    fabricImage.clipPath = clipRect;

    fabricCanvasInstanceRef.current.renderAll();
  }

  if ( item.style[ "border-bottom" ] ) {
    const borderWidth = parseFloat( item.style.border.split( " " )[ 0 ] ) * scale;
    const borderBottomWidth =
      parseFloat( item.style[ "border-bottom" ].split( " " )[ 0 ] ) * scale;
    const borderBottomColor = item.style[ "border-bottom" ].split( " " )[ 2 ];

    const deltaX =
      Math.sin( (containerRotation * Math.PI) / 180 ) *
      (borderBottomWidth / 2 - borderWidth / 2);
    const deltaY =
      Math.cos( (containerRotation * Math.PI) / 180 ) *
      (borderBottomWidth / 2 - borderWidth / 2);

    const adjustedLeft = hPosition - deltaX;
    const adjustedTop = vPosition + deltaY;

    const pathData = `
    M ${ -clipRect.width / 2 - borderWidth } ${
      -clipRect.height / 2 - borderWidth
    }
    H ${ clipRect.width / 2 + borderWidth }
    V ${ clipRect.height / 2 + borderBottomWidth }
    H ${ -clipRect.width / 2 - borderWidth }
    Z
  `;

    const customStroke = new fabric.Path( pathData, {
      left: adjustedLeft,
      top: adjustedTop,
      fill: borderBottomColor,
      originX: "center",
      originY: "center",
      angle: containerRotation,
      selectable: false,
      evented: false,
      idx: fabricImage.idx
    } );

    fabricCanvasInstanceRef.current.add( customStroke );
  }

  fabricCanvasInstanceRef.current.on( "mouse:down", ( opt ) => {
    const pointer = fabricCanvasInstanceRef.current.getPointer( opt.e );
    const targetObject = fabricCanvasInstanceRef.current.findTarget( opt.e );

    if ( targetObject === fabricImage ) {
      const isInsideClipRect = checkIfInsideClipRect( pointer, clipRect );

      if ( !isInsideClipRect ) {
        opt.e.preventDefault();
        fabricCanvasInstanceRef.current.discardActiveObject();
        fabricCanvasInstanceRef.current.requestRenderAll();
      }
    }
  } );

  let selectedRect;
  fabricImage.on( "selected", () => {

    if ( !selectedRect ) {
      const baseConfig = getBaseShapeConfig( containerRotation, clipRect );

      if ( hasCustomCorners ) {
        const pathData = createCustomRectPath( width, height, {
          topLeft: leftTopRadius,
          topRight: rightTopRadius,
          bottomLeft: leftBottomRadius,
          bottomRight: rightBottomRadius
        } );

        selectedRect = new fabric.Path( pathData, {
          ...baseConfig,
          left: hPosition,
          top: vPosition,
          width: clipRect.width,
          height: clipRect.height
        } );

        selectedRect.isSelectionBorder = true;

        addClipPath( fabricImage, selectedRect );
        fabricCanvasInstanceRef.current.renderAll();
      } else {
        selectedRect = new fabric.Rect( {
          ...baseConfig,
          left: clipRect.left,
          top: clipRect.top,
          width: clipRect.width,
          height: clipRect.height
        } );

        selectedRect.isSelectionBorder = true;

        const newRadius =
          typeof item.style["border-radius"] === "string" &&
          item.style["border-radius"].includes("%")
            ? (parseFloat(item.style["border-radius"]) / 100) *
              Math.min(clipRect.width, clipRect.height)
            : item.style["border-radius"] > 100
            ? parseFloat(item.style["border-radius"]) * scale
            : 0;

        updateRadiusProperties( clipRect, selectedRect, selectedLayout, newRadius );
        fabricCanvasInstanceRef.current.renderAll();
      }

    }
    fabricCanvasInstanceRef.current.add( selectedRect );

    fabricCanvasInstanceRef.current.renderAll();
    initialState = {
      left: fabricImage.left,
      top: fabricImage.top,
      angle: fabricImage.angle,
      width: fabricImage.width,
      height: fabricImage.height,
      scaleX: fabricImage.scaleX,
      scaleY: fabricImage.scaleY,
      flipX: fabricImage.flipX,
      flipY: fabricImage.flipY,
      src: fabricImage.src
    };
  } );

  fabricImage.on( "deselected", () => {
    if ( selectedRect ) {
      fabricCanvasInstanceRef.current.remove( selectedRect );
      selectedRect = null;
    }
    if ( fabricImage.clipPath ) {
      fabricImage.clipPath.set( {
        stroke: 0,
        strokeWidth: 0,
        strokeDashArray: 0
      } ); // Déassocié le clipPath de l'image
    }
    fabricCanvasInstanceRef.current.renderAll();
  } );

  fabricImage.on( "moving", () => {
    fabricImage.setCoords();

    const boundingRect = fabricImage.getBoundingRect();

    // Centre et dimensions du conteneur
    const clipCenter = new fabric.Point( clipRect.left, clipRect.top );
    const clipHalfWidth = clipRect.width / 2;
    const clipHalfHeight = clipRect.height / 2;

    // Angle du conteneur (rotation)
    const containerAngle = fabric.util.degreesToRadians( clipRect.angle || 0 );

    // Calcul des coins transformés du conteneur
    const clipTopLeft = fabric.util.rotatePoint(
      new fabric.Point( clipCenter.x - clipHalfWidth, clipCenter.y - clipHalfHeight ),
      clipCenter,
      containerAngle
    );
    const clipTopRight = fabric.util.rotatePoint(
      new fabric.Point( clipCenter.x + clipHalfWidth, clipCenter.y - clipHalfHeight ),
      clipCenter,
      containerAngle
    );
    const clipBottomLeft = fabric.util.rotatePoint(
      new fabric.Point( clipCenter.x - clipHalfWidth, clipCenter.y + clipHalfHeight ),
      clipCenter,
      containerAngle
    );
    const clipBottomRight = fabric.util.rotatePoint(
      new fabric.Point( clipCenter.x + clipHalfWidth, clipCenter.y + clipHalfHeight ),
      clipCenter,
      containerAngle
    );

    // Limites du conteneur
    const clipLeft = Math.min( clipTopLeft.x, clipTopRight.x, clipBottomLeft.x, clipBottomRight.x );
    const clipRight = Math.max( clipTopLeft.x, clipTopRight.x, clipBottomLeft.x, clipBottomRight.x );
    const clipTop = Math.min( clipTopLeft.y, clipTopRight.y, clipBottomLeft.y, clipBottomRight.y );
    const clipBottom = Math.max( clipTopLeft.y, clipTopRight.y, clipBottomLeft.y, clipBottomRight.y );

    // Ajustement pour empêcher le déplacement hors des limites
    if ( boundingRect.left > clipLeft ) {
      fabricImage.left += clipLeft - boundingRect.left;
    }
    if ( boundingRect.top > clipTop ) {
      fabricImage.top += clipTop - boundingRect.top;
    }
    if ( boundingRect.left + boundingRect.width < clipRight ) {
      fabricImage.left -= (boundingRect.left + boundingRect.width) - clipRight;
    }
    if ( boundingRect.top + boundingRect.height < clipBottom ) {
      fabricImage.top -= (boundingRect.top + boundingRect.height) - clipBottom;
    }

    fabricImage.setCoords();
  } );

  const debouncedCheck = checkChangesAndDispatch(
    fabricImage,
    dispatch,
    selectedLayout,
    fabricCanvasInstanceRef
  );

  const checkInterval = setInterval( debouncedCheck, DEBOUNCE_TIME );
  fabricImage.on( "removed", () => {
    clearInterval( checkInterval );
  } );

  if ( placeholder ) {
    fabricCanvasInstanceRef.current.add( fabricImage );
  } else {
    fabricCanvasInstanceRef.current.add( fabricImage );
  }
};

/**
 * Creates a canvas image element with the provided image, item, scale, radius, and other parameters.
 *
 * @param img - The source URL of the image to be displayed.
 * @param itemImage - The DOM element that the image is associated with.
 * @param scale - The scale factor to apply to the image.
 * @param radius - The radius to use for the image's border.
 * @param fabricCanvasInstanceRef - A reference to the Fabric.js canvas instance.
 * @param dispatch - A function to dispatch actions.
 * @param selectedLayout - The currently selected layout.
 * @param index - The index of the image in the layout.
 * @param premadeItem
 * @param placeholderRect - Whether to add a placeholder rectangle to the canvas.
 * @returns A Promise that resolves to the created Fabric.js image instance.
 */
export const createCanvasImage = async ( img,
  itemImage,
  scale,
  radius,
  fabricCanvasInstanceRef,
  dispatch,
  selectedLayout,
  index,
  placeholderRect = true ) => {

  const imageElement = new Image();
  const transformMatch = itemImage && itemImage.style && itemImage.style.transform && itemImage.style.transform.match( /rotate\(([-\d.]+)deg\)/ );
  const containerRotation = transformMatch ? parseFloat( transformMatch[ 1 ] ) : 0;

  const { hPosition, vPosition, width, height } = calculatePosition( itemImage, scale, fabricCanvasInstanceRef );

      imageElement.src = img;

  await new Promise<void>( ( resolve, reject ) => {
    imageElement.onload = () => resolve();
    imageElement.onerror = () => reject( new Error( `Image failed to load: ${ img }` ) );
  } );


  const placeholder = img === placeholderBase64;

  const naturalWidth = imageElement.naturalWidth;
  const naturalHeight = imageElement.naturalHeight;

  const widthScaleFactor = width / naturalWidth;
  const heightScaleFactor = height / naturalHeight;
  const scaleFactor = Math.max( widthScaleFactor, heightScaleFactor );
  const scaleX = scaleFactor;
  const scaleY = scaleFactor;
  const borderRadius = parseFloat( itemImage.style[ "border-radius" ] );

  const fabricImage = new FabricImage( imageElement, {
    left: hPosition,
    top: vPosition,
    scaleX: scaleX,
    scaleY: scaleY,
    originX: "center",
    originY: "center",
    angle: containerRotation,
    flipX: false,
    flipY: false,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
    hasControls: false,
    hasBorders: false,
    lockMovementX: placeholder,
    lockMovementY: placeholder,
    hoverCursor: "pointer",
    idx: index,
    placeholderImage: placeholder,
    perPixelTargetFind: true
  } );
  configureFabricImage( fabricImage,
    itemImage,
    scale,
    fabricCanvasInstanceRef,
    borderRadius,
    dispatch,
    selectedLayout,
    placeholderRect );

  return fabricImage;
};

/**
 * Creates a new canvas image element using the provided fabric element and configuration.
 *
 * @param itemImage - The HTML element containing the image to be rendered on the canvas.
 * @param scale - The scale factor to apply to the image.
 * @param radius - The radius to apply to the image.
 * @param fabricCanvasInstanceRef - A reference to the Fabric.js canvas instance.
 * @param fabricElement - The Fabric.js element representing the image.
 * @param dispatch - A function to dispatch actions.
 * @param selectedLayout - The currently selected layout.
 * @param index - The index of the image in the canvas.
 * @returns A Promise that resolves to the created Fabric.js image instance.
 */
export const createCanvasImageExisting = async ( itemImage,
  scale,
  radius,
  fabricCanvasInstanceRef,
  fabricElement,
  dispatch,
  selectedLayout,
  index ) => {
  const renderWidth = parseFloat( itemImage.style.width ) * scale;
  const renderHeight = parseFloat( itemImage.style.height ) * scale;
  const savedWidth = fabricElement.width * fabricElement.scaleX;
  const savedHeight = fabricElement.height * fabricElement.scaleY;
  const newScaleWidth = renderWidth / savedWidth;
  const newScaleHeight = renderHeight / savedHeight;

  const newScale = Math.max( newScaleWidth, newScaleHeight );

  const imageElement = new Image();
  imageElement.crossOrigin = "anonymous";
  const transformMatch = itemImage.style.transform && itemImage.style.transform.match( /rotate\(([-\d.]+)deg\)/ );
  const containerRotation = transformMatch ? parseFloat( transformMatch[ 1 ] ) : 0;

  const { hPosition, vPosition, width, height } = calculatePosition( itemImage, scale, fabricCanvasInstanceRef );

  imageElement.src = fabricElement.src;

  await new Promise<void>( ( resolve, reject ) => {
    imageElement.onload = () => resolve();
    imageElement.onerror = () => reject( new Error( `Image failed to load: ${ fabricElement.src }` ) );
  } );

  const placeholder = fabricElement.src === placeholderBase64;

  const naturalWidth = imageElement.naturalWidth;
  const naturalHeight = imageElement.naturalHeight;

  const widthScaleFactor = width / naturalWidth;
  const heightScaleFactor = height / naturalHeight;
  const scaleFactor = Math.max( widthScaleFactor, heightScaleFactor ) * newScale;
  const scaleX = scaleFactor;
  const scaleY = scaleFactor;

  const offsetX = (width - naturalWidth * scaleX) / 2;
  const offsetY = (height - naturalHeight * scaleY) / 2;

  const fabricImage = new FabricImage( imageElement, {
    left: hPosition + offsetX,
    top: vPosition + offsetY,
    scaleX: scaleX,
    scaleY: scaleY,
    originX: "center",
    originY: "center",
    backgroundColor: process.env.FILL_COLOR,
    angle: containerRotation,
    flipX: false,
    flipY: false,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
    hasControls: false,
    hasBorders: false,
    idx: index,
    lockMovementX: placeholder,
    lockMovementY: placeholder,
    hoverCursor: "pointer",
    placeholderImage: placeholder,
    perPixelTargetFind: true
  } );

  configureFabricImage( fabricImage,
    itemImage,
    scale,
    fabricCanvasInstanceRef,
    fabricElement.clipPath.rx,
    dispatch,
    selectedLayout,
    placeholder && true );

    if ( !placeholder ) {
    fabricImage.set( {
      top: fabricElement.top,
      left: fabricElement.left,
      scaleX: fabricElement.scaleX,
      scaleY: fabricElement.scaleY,
      angle: fabricElement.angle,
      flipX: fabricElement.flipX,
      flipY: fabricElement.flipY
    } );
  }
  checkAndAdjustRotation( fabricImage, fabricImage.clipPath, fabricImage.angle );
  replaceImageInContainer( fabricImage, fabricImage.clipPath );

  fabricCanvasInstanceRef.current.add(fabricImage);
  fabricCanvasInstanceRef.current?.renderAll();

  return fabricImage;

};

export const recreateImage = async ( itemImage,
  scale,
  radius,
  fabricCanvasInstanceRef,
  dispatch,
  selectedLayout,
  index,
  premadeItem ) => {
  const existingData = store.getState().editor.existingData;
  const layoutId = store.getState().editor.currentData.paramGeneral.layoutId;

  const transformMatch = itemImage.style.transform && itemImage.style.transform.match( /rotate\(([-\d.]+)deg\)/ );
  const containerRotation = transformMatch ? parseFloat( transformMatch[ 1 ] ) : 0;

  const borderRadius = parseFloat( itemImage.style[ "border-radius" ] );

  const { hPosition, vPosition, width, height } = calculatePosition( itemImage, scale, fabricCanvasInstanceRef );

  const currentProperties = premadeItem
                            ? existingData?.data?.dataImgCard?.find( ( item ) => item.index === premadeItem.index )
                            : null;
  const isSameSize = (
    Math.abs(existingData.data.paramGeneral.existingCanvasWidth - fabricCanvasInstanceRef.current.width) <= 3 &&
    Math.abs(existingData.data.paramGeneral.existingCanvasHeight - fabricCanvasInstanceRef.current.height) <= 3
  );
  const isSameLayout = (existingData.data.paramGeneral.layoutId === layoutId);

  const imageSrc = premadeItem.base64;

  const imageElement = new Image();
  imageElement.crossOrigin = "anonymous";

  const emptyImage = new FabricImage( imageElement, {
    left: hPosition,
    top: vPosition,
    originX: "center",
    originY: "center",
    angle: containerRotation,
    flipX: false,
    flipY: false,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
    hasControls: false,
    hasBorders: false,
    hoverCursor: "pointer",
    idx: index
  } );

  const newImage = await handleFileChange( imageSrc, dispatch, fabricCanvasInstanceRef, emptyImage );

  const angle = currentProperties ? currentProperties.data.rotate : 0;
  const flipX = currentProperties ? currentProperties.data.flipX : false;
  const flipY =  currentProperties ? currentProperties.data.flipY : false;

  const left = isSameLayout && currentProperties ? currentProperties.data.x * scale : hPosition;
  const top = isSameLayout && currentProperties ? currentProperties.data.y * scale : vPosition;

  const widthScaleFactor = width / newImage.width;
  const heightScaleFactor = height / newImage.height;
  const scaleFactor = Math.max( widthScaleFactor, heightScaleFactor );

  const adjustScaleX = isSameSize && isSameLayout ? currentProperties.data.scaleX : scaleFactor;
  const adjustScaleY = isSameSize && isSameLayout ? currentProperties.data.scaleY : scaleFactor;

  newImage.set( {
    angle: containerRotation/*  + angle */,
    flipX: flipX,
    flipY: flipY,
    left: left,
    top: top,
    scaleX: adjustScaleX,
    scaleY: adjustScaleY,
    selectable: true,
    evented: true,
    hoverCursor: 'pointer',
    perPixelTargetFind: true
  } );

  configureFabricImage( newImage,
    itemImage,
    scale,
    fabricCanvasInstanceRef,
    borderRadius,
    dispatch,
    selectedLayout,
    true,
    true
  );

  newImage.on( "mousedown", ( event ) => {
    event.e.stopPropagation();
    handleDisplayToolbar( dispatch, "image", index );
  } );

  checkAndAdjustRotation( newImage, newImage.clipPath, angle );
  replaceImageInContainer( newImage, newImage.clipPath );

  fabricCanvasInstanceRef.current.add(newImage);
  fabricCanvasInstanceRef.current?.renderAll();

  return newImage;
};

export async function createBackground(selectedBackground, fabricCanvasInstanceRef, scale) {
  try {
    const imageElement = new Image();
    
    await new Promise((resolve, reject) => {
      imageElement.onload = resolve;
      imageElement.onerror = () => {
        console.error("Failed to load image:", selectedBackground);
        reject(new Error(`Failed to load background image: ${selectedBackground}`));
      };
      imageElement.src = selectedBackground;
    });

    const fabricImage = new FabricImage(imageElement, {
      left: 0,
      top: 0,
      scaleX: scale,
      scaleY: scale,
      originX: 'left',
      originY: 'top',
      selectable: false,
      evented: false
    });

    fabricCanvasInstanceRef.set({ backgroundImage: fabricImage });
    fabricCanvasInstanceRef.requestRenderAll();

    return fabricImage;
  } catch (error) {
    console.error("Error in createBackground:", error);
    return null;
  }
}